import { useState, useEffect, useCallback, useRef } from "react";
import Icon from "@mdi/react";
import { mdiTrashCan, mdiInformationOutline, mdiInformation, mdiEmailOutline, mdiEyeOutline, mdiPencilOutline, mdiPlus, mdiAlertOutline, mdiMapMarker } from "@mdi/js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AppHeader from "../components/AppHeader";
import SchoolCard from "../components/SchoolCard";
import LocationPickerModal from "../components/LocationPickerModal";
import ConfirmationModal from "../components/ConfirmationModal";
import CustomFormInput from "../components/forms/CustomFormInput";
import { organizationApi } from "../api/organization";
import { SUPPLIER_POLICY_EDITOR_FIELDS } from "../components/SupplierPolicyAcknowledgement";
import { instructorApi } from "../api/instructor";
import { accountApi } from "../api/account";
import { paymentApi } from "../api/payment";
import { cacheImage, getCachedImage, clearCachedImage, base64ToBlobUrl } from "../utils/imageCache";
import ImageUpload from "../components/shared/ImageUpload";
import EmailTemplatePreviewModal from "../components/communications/EmailTemplatePreviewModal";
import EmailTemplateEditorModal from "../components/communications/EmailTemplateEditorModal";
import BaseModal from "../components/BaseModal";
import EmailTemplateEditor from "../components/communications/EmailTemplateEditor";
import EmailFiringInfoModal from "../components/communications/EmailFiringInfoModal";
import "../components/communications/EmailFiringInfoModal.css";
import { tokensToHandlebars } from "../utils/emailTokens";
import BankTransferAddonCard from "../components/billing/BankTransferAddonCard";
import BankDetailsForm from "../components/billing/BankDetailsForm";
import { billingApi } from "../api/billing";
import { resolveToId } from "../config/danceStyles";
import DanceStyleChips from "../components/DanceStyleChips";
import { currencyOptions } from "../config/currencyOptions";
import "./Common.css";
import "./Dashboard.css";
import "./header.css";
/**
 * Settings Page - User-Type-Aware Settings Management
 *
 * Displays relevant settings based on user.accountType:
 * - 'organisation': Full organizational settings (ticketing, payment, org info)
 * - 'instructor': Instructor-specific settings (coming soon)
 * - 'user': Standard user settings (coming soon)
 *
 * MIGRATION NOTE (2026-02-06): Migrated to CustomFormInput component
 * Part of DL-04FEB26-01: CustomFormInput migration
 *
 * Dance styles are sourced from the canonical config (via DanceStyleChips) — the
 * picker stores canonical ids in businessDanceStyles and resolves id -> label
 * for display and id -> colour for the selected chip.
 */

// Normalise any stored dance-style values to canonical ids and de-dupe. Guards
// against legacy/mixed-case residue (e.g. "Zouk" + "zouk") that the id-based
// picker can't otherwise clean — applied on both load and save so the field is
// always a clean, de-duped id list.
const cleanStyleIds = arr => [...new Set((arr || []).map(s => resolveToId(s) || s))];

const Settings = () => {
    const { user, checkPermission, organisation, refreshOrganisation, checkFeature, logout } = useAuth();
    const navigate = useNavigate();

    // Settings accordion state
    const [settingsAccordion, setSettingsAccordion] = useState({
        whatYouDo: false,
        personalDetails: false,
        paymentSettings: false,
        brandingSettings: false,
        organisationInfo: false,
        generalSettings: false,
        teamMembers: false,
        emailTemplates: false,
        supplierPolicies: false,
        accountDeletion: false,
    });

    // null while loading; { canDelete, code, message, supportEmail, blockingOrg } once resolved
    const [deletionEligibility, setDeletionEligibility] = useState(null);
    const [isRequestingAssistance, setIsRequestingAssistance] = useState(false);

    // Supplier buyer-facing policies (private lessons + class enrolments). Loaded
    // resolved (stored merged over platform defaults) on mount; section-local save.
    const [policies, setPolicies] = useState(null);
    const [isSavingPolicies, setIsSavingPolicies] = useState(false);

    // What you do — local toggle state, committed by one Save. null until the org profile has
    // loaded: an all-ticked placeholder would show wrong answers and be overwritten mid-edit.
    const [features, setFeatures] = useState(null);
    const [isSavingFeatures, setIsSavingFeatures] = useState(false);

    // Email templates state
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null); // { emailType, label }
    const [emailFiringInfo, setEmailFiringInfo] = useState(null); // null | 'editable' | 'automated'
    const [emailInfoHovered, setEmailInfoHovered] = useState(null); // null | 'editable' | 'automated'
    const [editTemplate, setEditTemplate] = useState(null); // { emailType, label }

    // Studio saved templates state
    const [studioTemplates, setStudioTemplates] = useState([]);
    const [studioTemplatesLoading, setStudioTemplatesLoading] = useState(false);
    const [editingStudioTemplate, setEditingStudioTemplate] = useState(null); // null | { id?, name, subject, bodyHtml }
    const [deletingStudioTemplate, setDeletingStudioTemplate] = useState(null); // null | { id, name }
    const [studioTemplateSaving, setStudioTemplateSaving] = useState(false);
    const studioEditorRef = useRef(null);

    // Stripe Connect status (for instructor Payment Account section)
    const [connectStatus, setConnectStatus] = useState(null);
    const [connectStatusLoading, setConnectStatusLoading] = useState(false);
    const [bankTransfer, setBankTransfer] = useState({
        offeringEnabled: false,
        bankDetails: { bsb: "", accountName: "", accountNumber: "" },
    });

    // Organization settings state
    const [, setIsLoadingSettings] = useState(false);
    const [orgSettings, setOrgSettings] = useState({ dashboardPrimaryCard: null });
    const [, setProfileImageUrl] = useState(null);
    const [, setIsLoadingProfileImage] = useState(false);

    // Template state — not implemented for MVP
    // const [availableTemplates, setAvailableTemplates] = useState([]);
    // const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    // const [showTemplateModal, setShowTemplateModal] = useState(false);

    // Home location state (bookings & payments settings)
    const [homeLocation, setHomeLocation] = useState(null);
    const [showLocationPicker, setShowLocationPicker] = useState(false);
    const [, setIsSavingLocation] = useState(false);

    // Confirmation modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalConfig, setConfirmModalConfig] = useState({
        title: "",
        message: "",
        confirmText: "Confirm",
        variant: "primary",
        onConfirm: null,
    });

    // Personal name state (account-level — applies to all user types)
    const [personalDetails, setPersonalDetails] = useState({
        firstName: user?.accountDetails?.firstName || "",
        lastName: user?.accountDetails?.lastName || "",
    });
    const [isSavingPersonalDetails, setIsSavingPersonalDetails] = useState(false);

    // Business/Organisation details state (top-level org document — separate from orgSettings)
    const [orgDetails, setOrgDetails] = useState(null);
    // Currency as loaded from the server. Base currency has its own guarded endpoint
    // (the generic org PATCH drops it), so we only call it when the value actually changed.
    const [savedCurrency, setSavedCurrency] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoChanged, setLogoChanged] = useState(false);
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [coverImageChanged, setCoverImageChanged] = useState(false);
    const [isSavingOrgDetails, setIsSavingOrgDetails] = useState(false);
    const [isSavingLogo, setIsSavingLogo] = useState(false);
    const [isSavingCover, setIsSavingCover] = useState(false);
    const [isRemovingCover, setIsRemovingCover] = useState(false);
    const [showBusinessLocationPicker, setShowBusinessLocationPicker] = useState(false);
    // True once the business location is changed in the picker; only then is it saved.
    const [orgLocationChanged, setOrgLocationChanged] = useState(false);

    // Team members state (org admin management)
    const [admins, setAdmins] = useState([]);
    const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
    const [adminSearchQuery, setAdminSearchQuery] = useState("");
    const [adminSearchResults, setAdminSearchResults] = useState([]);
    const [isSearchingAdmins, setIsSearchingAdmins] = useState(false);
    const [newAdminRole, setNewAdminRole] = useState("helper");

    // Branding state
    const [branding, setBranding] = useState({
        headerColor: "#451f78",
        headerTextColor: "#ffffff",
        backgroundColor: "#f5f0fb",
        textColor: "#333333",
        buttonColor: "#451f78",
        buttonTextColor: "#ffffff",
    });
    const [isSavingBranding, setIsSavingBranding] = useState(false);

    // Booking settings state (instructor)

    // Accordion toggle — exclusive open (closing all others when one opens)
    const toggleAccordionSection = sectionKey => {
        setSettingsAccordion(prev => {
            const allClosed = Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: false }), {});
            return { ...allClosed, [sectionKey]: !prev[sectionKey] };
        });
    };

    // Team members handlers
    const loadAdmins = async () => {
        setIsLoadingAdmins(true);
        try {
            const response = await organizationApi.getOrgAdmins(user.organisationId);
            if (response.ok) {
                setAdmins(response.data.admins || []);
            } else {
                toast.error("Failed to load team members");
            }
        } catch (err) {
            toast.error("Failed to load team members");
        } finally {
            setIsLoadingAdmins(false);
        }
    };

    const handleAdminSearch = useCallback(
        async (q, { showErrorToast = false } = {}) => {
            setAdminSearchQuery(q);
            if (q.length < 2) {
                setAdminSearchResults([]);
                return;
            }
            setIsSearchingAdmins(true);
            try {
                const users = await accountApi.searchAccounts(q);
                const existingIds = new Set(admins.map(a => a.accountId));
                const filtered = users.filter(u => !existingIds.has(u.id));
                setAdminSearchResults(filtered);
            } catch {
                if (showErrorToast) {
                    toast.error("Search failed. Please try again.");
                }
            } finally {
                setIsSearchingAdmins(false);
            }
        },
        [admins]
    );

    const handleAddAdmin = async targetAccountId => {
        try {
            const response = await organizationApi.addOrgAdmin(user.organisationId, targetAccountId, newAdminRole);
            if (response.ok) {
                toast.success("Team member added");
                setAdminSearchResults([]);
                setAdminSearchQuery("");
                setNewAdminRole("helper");
                await loadAdmins();
            } else {
                toast.error(response.data?.message || "Failed to add team member");
            }
        } catch (err) {
            toast.error("Failed to add team member");
        }
    };

    const handleChangeAdminRole = async (targetAccountId, newRole) => {
        try {
            const response = await organizationApi.updateOrgAdminRole(user.organisationId, targetAccountId, newRole);
            if (response.ok) {
                toast.success("Role updated");
                await loadAdmins();
            } else {
                toast.error(response.data?.message || "Failed to update role");
            }
        } catch (err) {
            toast.error("Failed to update role");
        }
    };

    const handleRemoveAdmin = (targetAccountId, displayName) => {
        const adminName = displayName || "this team member";
        setConfirmModalConfig({
            title: "Remove Team Member",
            message: `Are you sure you want to remove ${adminName}? They will no longer be able to log in and manage this account.`,
            confirmText: "Remove",
            variant: "danger",
            onConfirm: async () => {
                setShowConfirmModal(false);
                try {
                    const response = await organizationApi.removeOrgAdmin(user.organisationId, targetAccountId);
                    if (response.ok) {
                        toast.success("Team member removed");
                        setAdmins(prev => prev.filter(a => a.accountId !== targetAccountId));
                    } else {
                        toast.error(response.data?.message || "Failed to remove team member");
                    }
                } catch (err) {
                    toast.error("Failed to remove team member");
                }
            },
        });
        setShowConfirmModal(true);
    };

    // Load organization settings
    const loadOrganisationSettings = async () => {
        try {
            setIsLoadingSettings(true);

            const settings = await organizationApi.getSettings(user.organisationId);

            setOrgSettings(settings);

            // Load org-level branding
            const brandingResp = await organizationApi.getBranding();
            if (brandingResp.ok && brandingResp.data?.branding) {
                setBranding(prev => ({ ...prev, ...brandingResp.data.branding }));
            }
        } catch (error) {
            console.error("Error loading organisation settings:", error);
        } finally {
            setIsLoadingSettings(false);
        }
    };

    // Load org profile details (top-level org document — name, logo, location, blurb, dance styles)
    const loadOrgDetails = async () => {
        try {
            const org = await organizationApi.getOrgProfile(user.organisationId);
            setSavedCurrency(org.currency || "AUD");
            const details = {
                name: org.name || "",
                currency: org.currency || "AUD",
                businessBlurb: org.businessBlurb || "",
                // Resolve any stored value (canonical id, or legacy label/alias from
                // pre-migration data) to a canonical id and de-dupe.
                businessDanceStyles: cleanStyleIds(org.businessDanceStyles),
                orgLocation: org.orgLocation || null,
                orgImageKey: org.orgImageKey || null,
                orgLogoVersion: org.orgLogoVersion || null,
                orgCoverVersion: org.orgCoverVersion || null,
                isSearchable: org.isSearchable !== false,
                isDanceSchool: org.isDanceSchool || false,
                isDanceArtist: org.isDanceArtist || false,
                isEventOrganiser: org.isEventOrganiser || false,
                logoUrl: null,
                coverImageUrl: null,
            };
            setOrgDetails(details);

            if (org.orgImageKey) {
                const logoCacheKey = `${org.orgImageKey}_v${org.orgLogoVersion}`;
                const cached = getCachedImage(logoCacheKey, "logo");
                if (cached) {
                    setOrgDetails(prev => ({ ...prev, logoUrl: base64ToBlobUrl(cached) }));
                } else {
                    const imgResp = await organizationApi.getProfileImageBase64(org.orgImageKey, { timeout: 30000 });
                    if (imgResp.ok && imgResp.data?.imageBase64) {
                        cacheImage(logoCacheKey, imgResp.data.imageBase64, "logo").catch(err =>
                            console.warn("Failed to cache logo:", err)
                        );
                        setOrgDetails(prev => ({ ...prev, logoUrl: base64ToBlobUrl(imgResp.data.imageBase64) }));
                    }
                }
            }

            // Load cover image from orgCoverImageKey (top-level field)
            const coverKey = org.orgCoverImageKey || null;
            if (coverKey) {
                const coverCacheKey = `${coverKey}_v${org.orgCoverVersion}`;
                const cachedCover = getCachedImage(coverCacheKey, "cover");
                if (cachedCover) {
                    setOrgDetails(prev => ({ ...prev, coverImageUrl: base64ToBlobUrl(cachedCover) }));
                } else {
                    const coverResp = await organizationApi.getProfileImageBase64(coverKey, { timeout: 30000 });
                    if (coverResp.ok && coverResp.data?.imageBase64) {
                        cacheImage(coverCacheKey, coverResp.data.imageBase64, "cover").catch(err =>
                            console.warn("Failed to cache cover image:", err)
                        );
                        setOrgDetails(prev => ({
                            ...prev,
                            coverImageUrl: base64ToBlobUrl(coverResp.data.imageBase64),
                        }));
                    }
                }
            }
        } catch (error) {
            console.error("Error loading org details:", error);
            toast.error("Could not load your account details. Please refresh the page.");
        }
    };

    // Save business/organisation details
    const handleSavePersonalDetails = async () => {
        if (!personalDetails.firstName || personalDetails.firstName.trim().length < 1) {
            toast.error("First name is required");
            return;
        }
        if (!personalDetails.lastName || personalDetails.lastName.trim().length < 1) {
            toast.error("Last name is required");
            return;
        }
        try {
            setIsSavingPersonalDetails(true);
            const accountId = user?.accountId;
            if (!accountId) throw new Error("No account ID");
            await accountApi.updateAccount(accountId, {
                firstName: personalDetails.firstName.trim(),
                lastName: personalDetails.lastName.trim(),
            });
            toast.success("Personal details saved!");
        } catch (error) {
            console.error("Error saving personal details:", error);
            // Surface the backend reason rather than a generic retry prompt, which
            // would hide failures the user could actually act on.
            toast.error(error.message || "Failed to save personal details. Please try again.");
        } finally {
            setIsSavingPersonalDetails(false);
        }
    };

    const handleSaveLogo = async () => {
        if (!logoFile?.compressedBase64) return;
        setIsSavingLogo(true);
        const prevOrgImageKey = orgDetails?.orgImageKey;
        const prevLogoVersion = orgDetails?.orgLogoVersion;
        const raw = logoFile.compressedBase64;
        const pureBase64 = raw.includes(",") ? raw.split(",")[1] : raw;
        const payload = {
            name: orgDetails?.name?.trim() || "",
            businessBlurb: orgDetails?.businessBlurb || "",
            businessDanceStyles: cleanStyleIds(orgDetails?.businessDanceStyles),
            base64Image: pureBase64,
        };
        const updated = await organizationApi.updateOrgProfile(user.organisationId, payload).catch(err => {
            toast.error(err?.message || "Failed to save logo. Please try again.");
            setIsSavingLogo(false);
            return null;
        });
        if (updated) {
            if (prevOrgImageKey) {
                const oldCacheKey = prevLogoVersion ? `${prevOrgImageKey}_v${prevLogoVersion}` : prevOrgImageKey;
                clearCachedImage(oldCacheKey, "logo");
            }
            if (updated.orgImageKey && updated.orgLogoVersion) {
                cacheImage(
                    `${updated.orgImageKey}_v${updated.orgLogoVersion}`,
                    logoFile.compressedBase64,
                    "logo"
                ).catch(err => console.warn("Failed to cache logo after upload:", err));
            }
            setOrgDetails(prev => ({
                ...prev,
                logoUrl: logoFile.thumbnail || logoFile.compressedBase64,
                orgImageKey: updated.orgImageKey || prev.orgImageKey,
                orgLogoVersion: updated.orgLogoVersion || prev.orgLogoVersion,
            }));
            setLogoFile(null);
            setLogoChanged(false);
            toast.success("Logo saved.");
        }
        setIsSavingLogo(false);
    };

    const handleCancelLogo = () => {
        setLogoFile(null);
        setLogoChanged(false);
    };

    const handleSaveCover = async () => {
        if (!coverImageFile?.compressedBase64) return;
        setIsSavingCover(true);
        const prevCoverKey = orgDetails?.orgCoverImageKey;
        const prevCoverVersion = orgDetails?.orgCoverVersion;
        const raw = coverImageFile.compressedBase64;
        const pureBase64 = raw.includes(",") ? raw.split(",")[1] : raw;
        const payload = {
            name: orgDetails?.name?.trim() || "",
            businessBlurb: orgDetails?.businessBlurb || "",
            businessDanceStyles: cleanStyleIds(orgDetails?.businessDanceStyles),
            base64CoverImage: pureBase64,
        };
        const updated = await organizationApi.updateOrgProfile(user.organisationId, payload).catch(err => {
            toast.error(err?.message || "Failed to save cover image. Please try again.");
            setIsSavingCover(false);
            return null;
        });
        if (updated) {
            if (prevCoverKey) {
                const oldCacheKey = prevCoverVersion ? `${prevCoverKey}_v${prevCoverVersion}` : prevCoverKey;
                clearCachedImage(oldCacheKey, "cover");
            }
            if (updated.orgCoverImageKey && updated.orgCoverVersion) {
                cacheImage(
                    `${updated.orgCoverImageKey}_v${updated.orgCoverVersion}`,
                    coverImageFile.compressedBase64,
                    "cover"
                ).catch(err => console.warn("Failed to cache cover after upload:", err));
            }
            setOrgDetails(prev => ({
                ...prev,
                coverImageUrl: coverImageFile.thumbnail || coverImageFile.compressedBase64,
                orgCoverImageKey: updated.orgCoverImageKey || prev.orgCoverImageKey,
                orgCoverVersion: updated.orgCoverVersion || prev.orgCoverVersion,
            }));
            setCoverImageFile(null);
            setCoverImageChanged(false);
            toast.success("Cover image saved.");
        }
        setIsSavingCover(false);
    };

    const handleCancelCover = () => {
        setCoverImageFile(null);
        setCoverImageChanged(false);
    };

    const handleRemoveCover = async () => {
        setIsRemovingCover(true);
        const prevCoverKey = orgDetails?.orgCoverImageKey;
        const prevCoverVersion = orgDetails?.orgCoverVersion;
        const payload = {
            name: orgDetails?.name?.trim() || "",
            businessBlurb: orgDetails?.businessBlurb || "",
            businessDanceStyles: cleanStyleIds(orgDetails?.businessDanceStyles),
            base64CoverImage: null,
        };
        const updated = await organizationApi.updateOrgProfile(user.organisationId, payload).catch(err => {
            toast.error(err?.message || "Failed to remove cover image. Please try again.");
            setIsRemovingCover(false);
            return null;
        });
        if (updated) {
            if (prevCoverKey) {
                const oldCacheKey = prevCoverVersion ? `${prevCoverKey}_v${prevCoverVersion}` : prevCoverKey;
                clearCachedImage(oldCacheKey, "cover");
            }
            setOrgDetails(prev => ({
                ...prev,
                coverImageUrl: null,
                orgCoverImageKey: null,
                orgCoverVersion: null,
            }));
            toast.success("Cover image removed.");
        }
        setIsRemovingCover(false);
    };

    // Visibility toggle auto-saves on change (matches InstructorProfilePage handleToggleProfileVisibility pattern).
    const handleToggleSchoolVisibility = async e => {
        const newVisibility = e.target.checked;
        const previousVisibility = orgDetails?.isSearchable !== false;
        setOrgDetails(prev => ({ ...prev, isSearchable: newVisibility }));
        try {
            await organizationApi.updateOrgProfile(user.organisationId, { isSearchable: newVisibility });
            toast.success(newVisibility ? "School is now visible to students" : "School hidden from students");
        } catch (err) {
            console.error("Error updating school visibility:", err);
            setOrgDetails(prev => ({ ...prev, isSearchable: previousVisibility }));
            toast.error(err.message || "Failed to update school visibility");
        }
    };

    const handleSaveOrgDetails = async () => {
        if (!orgDetails?.name || orgDetails.name.trim().length < 2) {
            toast.error("Name must be at least 2 characters");
            return;
        }
        if (orgDetails.businessBlurb && orgDetails.businessBlurb.length > 500) {
            toast.error("Description must be 500 characters or less");
            return;
        }
        try {
            setIsSavingOrgDetails(true);
            // NOTE: currency is deliberately NOT in this payload. It is bound to the
            // connected account's country and has its own guarded endpoint; the generic
            // org PATCH is server-side whitelisted and would silently drop it.
            const payload = {
                name: orgDetails.name.trim(),
                businessBlurb: orgDetails.businessBlurb || "",
                businessDanceStyles: cleanStyleIds(orgDetails.businessDanceStyles),
            };
            // isSearchable is deliberately NOT in this payload. It auto-saves on click via
            // handleToggleSchoolVisibility, so echoing local state here adds nothing.
            // Only send the location when it was changed: every send is re-geocoded server-side.
            // The picker's coordinates go with it as the fallback if that geocode fails.
            if (orgLocationChanged && orgDetails.orgLocation?.description) {
                const { description, latitude, longitude } = orgDetails.orgLocation;
                payload.orgLocation = { description, latitude, longitude };
            }
            // Images are saved separately via handleSaveLogo / handleSaveCover
            const nextCurrency = orgDetails.currency || "AUD";
            if (nextCurrency !== savedCurrency) {
                // Own endpoint, own failure mode: the backend rejects a currency that no
                // longer settles in the linked payout account's country. Surface that
                // message and keep the rest of the save going.
                try {
                    await organizationApi.updateOrgCurrency(user.organisationId, nextCurrency);
                    setSavedCurrency(nextCurrency);
                } catch (err) {
                    toast.error(err.message || "Could not update base currency");
                    setOrgDetails(prev => ({ ...prev, currency: savedCurrency }));
                }
            }
            const updated = await organizationApi.updateOrgProfile(user.organisationId, payload);
            setOrgDetails(prev => ({
                ...prev,
                name: updated.name,
                currency: updated.currency || "AUD",
                businessBlurb: updated.businessBlurb || "",
                businessDanceStyles: cleanStyleIds(updated.businessDanceStyles),
                orgLocation: updated.orgLocation || null,
                orgImageKey: updated.orgImageKey || null,
                orgCoverImageKey: updated.orgCoverImageKey || null,
                orgLogoVersion: updated.orgLogoVersion || null,
                orgCoverVersion: updated.orgCoverVersion || null,
            }));
            if (logoChanged && updated.orgImageKey) {
                const oldLogoKey = orgDetails?.orgImageKey
                    ? orgDetails.orgLogoVersion
                        ? `${orgDetails.orgImageKey}_v${orgDetails.orgLogoVersion}`
                        : orgDetails.orgImageKey
                    : null;
                if (oldLogoKey) clearCachedImage(oldLogoKey, "logo");
                if (logoFile?.compressedBase64 && updated.orgLogoVersion) {
                    cacheImage(
                        `${updated.orgImageKey}_v${updated.orgLogoVersion}`,
                        logoFile.compressedBase64,
                        "logo"
                    ).catch(err => console.warn("Failed to cache logo after update:", err));
                    setOrgDetails(prev => ({ ...prev, logoUrl: logoFile.compressedBase64 }));
                }
                setLogoChanged(false);
                setLogoFile(null);
            }
            if (coverImageChanged && updated.orgCoverImageKey) {
                const oldCoverKey = orgDetails?.orgCoverImageKey
                    ? orgDetails.orgCoverVersion
                        ? `${orgDetails.orgCoverImageKey}_v${orgDetails.orgCoverVersion}`
                        : orgDetails.orgCoverImageKey
                    : null;
                if (oldCoverKey) clearCachedImage(oldCoverKey, "cover");
                if (coverImageFile?.compressedBase64 && updated.orgCoverVersion) {
                    cacheImage(
                        `${updated.orgCoverImageKey}_v${updated.orgCoverVersion}`,
                        coverImageFile.compressedBase64,
                        "cover"
                    ).catch(err => console.warn("Failed to cache cover image after update:", err));
                    setOrgDetails(prev => ({ ...prev, coverImageUrl: coverImageFile.compressedBase64 }));
                }
                setCoverImageChanged(false);
                setCoverImageFile(null);
            }
            setOrgLocationChanged(false);
            toast.success("Account details saved.");
        } catch (error) {
            console.error("Error saving org details:", error);
            toast.error(error.message || "Failed to save account details. Please try again.");
        } finally {
            setIsSavingOrgDetails(false);
        }
    };

    // Template functions — not implemented for MVP
    // const loadAvailableTemplates = async () => { ... };
    // const handleCreateTemplate = () => { ... };
    // const handleLoadTemplate = async (templateId) => { ... };

    const loadBankTransferState = async () => {
        if (!user?.organisationId) return;
        try {
            const data = await billingApi.getAddonState();
            setBankTransfer(data);
        } catch (error) {
            console.error("Error loading bank transfer state:", error);
        }
    };

    const loadConnectStatus = async () => {
        if (!user?.organisationId) return;
        try {
            setConnectStatusLoading(true);
            const data = await paymentApi.getConnectStatus(user.organisationId);
            setConnectStatus(data);
        } catch (error) {
            console.error("Error loading connect status:", error);
        } finally {
            setConnectStatusLoading(false);
        }
    };

    // Featured dashboard card — save immediately on selection
    const handleHeroCardChange = async newCard => {
        const prev = orgSettings?.dashboardPrimaryCard || null;
        setOrgSettings(s => ({ ...s, dashboardPrimaryCard: newCard }));
        try {
            await organizationApi.updateDashboardCard(user.organisationId, newCard);
        } catch (err) {
            setOrgSettings(s => ({ ...s, dashboardPrimaryCard: prev }));
            toast.error(err.message || "Failed to save preference");
        }
    };

    // Whether THIS user may delete their account. Resolved server-side up front so the
    // section can state the outcome instead of offering a button that 409s on click
    // (a sole owner of a school with active records cannot delete).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await accountApi.getDeletionEligibility();
                if (!cancelled && response.ok && response.data?.eligibility) {
                    setDeletionEligibility(response.data.eligibility);
                }
            } catch (err) {
                console.error("Failed to load account deletion eligibility:", err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Keep the local feature-toggle state in sync with the hydrated org profile. A missing
    // features object (or key) reads as on, matching the backend's fillFeatures.
    useEffect(() => {
        if (!organisation) return;
        const f = organisation.settings?.features || {};
        setFeatures({
            classManagement: f.classManagement !== false,
            privateLessons: f.privateLessons !== false,
            ticketing: f.ticketing !== false,
            bulkEmail: f.bulkEmail !== false,
        });
    }, [organisation]);

    // Load the Supplier's buyer-facing policies once the org id is available. The
    // response is resolved (defaults merged in), so the editor always shows complete
    // text even for an org that has never customised them.
    useEffect(() => {
        if (!user?.organisationId) return;
        let cancelled = false;
        (async () => {
            try {
                const p = await organizationApi.getOrgPolicies(user.organisationId);
                if (!cancelled) setPolicies(p);
            } catch (err) {
                console.error("Failed to load supplier policies:", err);
            }
        })();
        return () => { cancelled = true; };
    }, [user?.organisationId]);

    // What you do — the single place an org says what it does. Toggles update local state
    // only; Save sends the changed keys in one PATCH. The server derives the org type flags
    // (isDanceSchool / isDanceArtist / isEventOrganiser) in the same write, may clear the
    // Featured Dashboard card, and seeds the instructor profile when private lessons are
    // switched on for the first time. Turning a domain off closes it to the public, so that
    // direction asks for confirmation first.
    const handleFeatureToggle = (key, checked) => {
        setFeatures(f => ({ ...f, [key]: checked }));
    };

    const WHAT_YOU_DO_ROWS = [
        { key: "privateLessons", label: "Offer private lessons", hint: "Manage private lesson requests and your availability." },
        { key: "classManagement", label: "Run classes", hint: "Manage weekly classes, terms, enrolments and attendance." },
        { key: "ticketing", label: "Sell event tickets", hint: "Sell event tickets and manage guest check-in." },
    ];

    // What the public can no longer start once a domain is off (confirmation copy).
    const DOMAIN_CLOSE_COPY = {
        classManagement: { name: "classes", closes: "enrolments, enquiries and trial purchases" },
        privateLessons: { name: "private lessons", closes: "private lesson bookings" },
        ticketing: { name: "event tickets", closes: "ticket sales" },
    };

    const joinWithAnd = items =>
        items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

    const saveWhatYouDo = async patch => {
        setIsSavingFeatures(true);
        try {
            const res = await organizationApi.updateOrgFeatures(user.organisationId, patch);
            setFeatures(res.features);
            setOrgDetails(prev => ({
                ...prev,
                isDanceSchool: res.isDanceSchool,
                isDanceArtist: res.isDanceArtist,
                isEventOrganiser: res.isEventOrganiser,
            }));
            setOrgSettings(s => ({ ...s, dashboardPrimaryCard: res.dashboardPrimaryCard }));
            localStorage.setItem("dl_dashboardPrimaryCard", res.dashboardPrimaryCard ?? "null");
            await refreshOrganisation();
            toast.success(res.instructorProfileSeeded
                ? "Your changes are saved. Your instructor profile is ready to fill in."
                : "Your changes are saved.");
        } catch (err) {
            toast.error(err.message || "Could not save your changes.");
        } finally {
            setIsSavingFeatures(false);
        }
    };

    const handleSaveWhatYouDo = () => {
        if (!features) return;
        const saved = organisation?.settings?.features || {};
        const patch = {};
        for (const key of ["classManagement", "privateLessons", "ticketing", "bulkEmail"]) {
            if (features[key] !== (saved[key] !== false)) patch[key] = features[key];
        }
        if (Object.keys(patch).length === 0) {
            toast.success("No changes to save.");
            return;
        }
        const closing = Object.keys(DOMAIN_CLOSE_COPY).filter(key => patch[key] === false);
        if (closing.length === 0) {
            saveWhatYouDo(patch);
            return;
        }
        setConfirmModalConfig({
            title: `Turn off ${joinWithAnd(closing.map(key => DOMAIN_CLOSE_COPY[key].name))}?`,
            message: `Your public pages will stop taking new ${joinWithAnd(closing.map(key => DOMAIN_CLOSE_COPY[key].closes))}. Anyone already enrolled, booked or holding a ticket keeps full access, and nothing is deleted. You can turn it back on at any time.`,
            confirmText: "Turn off",
            variant: "warning",
            onConfirm: () => {
                setShowConfirmModal(false);
                saveWhatYouDo(patch);
            },
        });
        setShowConfirmModal(true);
    };

    const renderWhatYouDo = () => (
        <div className="settings-accordion-section">
            <div
                className={`settings-accordion-header ${settingsAccordion.whatYouDo ? "expanded" : ""}`}
                onClick={() => toggleAccordionSection("whatYouDo")}
            >
                <div className="accordion-title-group">
                    <h4>What you do</h4>
                    <span className="accordion-title-hint">enable/disable lessons, classes, tickets</span>
                </div>
                <span className="accordion-icon">{settingsAccordion.whatYouDo ? "−" : "+"}</span>
            </div>
            {settingsAccordion.whatYouDo && (
                <div className="settings-accordion-content">
                    <p className="accordion-description">
                        Use one tool, all three, or anything in between. Turning one off closes it to the public, but keeps all your data.
                    </p>
                    {!features ? (
                        <p className="accordion-description">Loading...</p>
                    ) : (<>
                    {WHAT_YOU_DO_ROWS.map(row => (
                        <div key={row.key} style={{ marginBottom: "14px" }}>
                            <CustomFormInput
                                type="checkbox"
                                variant="purple"
                                id={`what-you-do-${row.key}`}
                                label={row.label}
                                hint={row.hint}
                                checked={features[row.key]}
                                onChange={e => handleFeatureToggle(row.key, e.target.checked)}
                            />
                        </div>
                    ))}
                    <div className="settings-subsection" style={{ marginTop: "20px" }}>
                        <p className="subsection-title">Add-ons</p>
                        <div style={{ marginBottom: "14px" }}>
                            <CustomFormInput
                                type="checkbox"
                                variant="purple"
                                id="what-you-do-bulkEmail"
                                label="Email students/customers"
                                hint="Compose and send announcements to your students/customers."
                                checked={features.bulkEmail}
                                onChange={e => handleFeatureToggle("bulkEmail", e.target.checked)}
                            />
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
                        <button className="btn-purple" onClick={handleSaveWhatYouDo} disabled={isSavingFeatures}>
                            {isSavingFeatures ? "Saving..." : "Save"}
                        </button>
                    </div>
                    </>)}
                </div>
            )}
        </div>
    );

    // Branding save handler
    const handleSaveBranding = async () => {
        const invalid = Object.entries(branding).filter(([, v]) => !/^#[0-9A-Fa-f]{6}$/.test(v));
        if (invalid.length > 0) {
            toast.error("All colours must be valid hex values (e.g. #451f78)");
            return;
        }
        setIsSavingBranding(true);
        try {
            const r = await organizationApi.updateBranding(branding);
            if (r.ok) {
                toast.success("Branding saved");
            } else {
                toast.error(r.data?.message || "Failed to save branding");
            }
        } catch {
            toast.error("Failed to save branding");
        } finally {
            setIsSavingBranding(false);
        }
    };

    // Section-local save for the Supplier buyer-facing policies. Sends both surfaces;
    // the backend persists each via dot-notation so sibling settings are preserved.
    const handleSavePolicies = async () => {
        if (!policies?.classEnrolments) return;
        setIsSavingPolicies(true);
        try {
            // Settings edits the class-enrolment surface only (partial save preserves
            // the private-lesson surface edited on the instructor profile page).
            const updated = await organizationApi.updateOrgPolicies(user.organisationId, {
                classEnrolments: policies.classEnrolments,
            });
            setPolicies(updated);
            toast.success("Terms and conditions saved");
        } catch (err) {
            toast.error(err.message || "Failed to save terms and conditions");
        } finally {
            setIsSavingPolicies(false);
        }
    };

    // Home location management (bookings & payments settings)
    const handleLocationSave = async locationData => {
        try {
            setIsSavingLocation(true);

            await instructorApi.addLocation(user.organisationId, {
                description: locationData.description,
                address: locationData.address,
                latitude: locationData.latitude,
                longitude: locationData.longitude,
            });

            setHomeLocation(locationData);
            setShowLocationPicker(false);
            toast.success("Home location saved successfully!");
            await loadOrganisationSettings(); // Reload to get updated data
        } catch (error) {
            console.error("Error saving home location:", error);
            toast.error(error.message || "Failed to save home location. Please try again.");
            throw error; // Re-throw to prevent modal from closing
        } finally {
            setIsSavingLocation(false);
        }
    };

    // Fetch organization profile image
    const fetchProfileImage = useCallback(async () => {
        const abortController = new AbortController();

        try {
            setIsLoadingProfileImage(true);

            if (orgSettings?.orgCoverImageKey) {
                const imageKey = orgSettings.orgCoverImageKey;
                const cacheKey = `${imageKey}_v${orgSettings.orgCoverVersion}`;

                const cachedUrl = getCachedImage(cacheKey, "profile");
                if (cachedUrl) {
                    setProfileImageUrl(base64ToBlobUrl(cachedUrl));
                    setIsLoadingProfileImage(false);
                    return abortController;
                }

                const response = await organizationApi.getProfileImageBase64(imageKey);

                if (abortController.signal.aborted) {
                    return;
                }

                if (response.ok && response.data.imageBase64) {
                    const base64DataUrl = response.data.imageBase64;
                    setProfileImageUrl(base64ToBlobUrl(base64DataUrl));

                    cacheImage(cacheKey, base64DataUrl, "profile").catch(err =>
                        console.warn("Failed to cache profile image:", err)
                    );
                } else {
                    console.warn("No Base64 image data received");
                }
            }
        } catch (error) {
            console.error("Error fetching profile image:", error);
        } finally {
            if (!abortController.signal.aborted) {
                setIsLoadingProfileImage(false);
            }
        }

        return abortController;
    }, [orgSettings?.orgCoverImageKey]);

    // Load settings on mount
    useEffect(() => {
        if (user?.organisationId) {
            loadOrganisationSettings();
            loadOrgDetails();
            loadConnectStatus();
            loadBankTransferState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.organisationId]); // Only re-run when organisationId changes

    // Load images when settings change
    useEffect(() => {
        let profileAbortController;

        if (orgSettings) {
            fetchProfileImage().then(controller => {
                profileAbortController = controller;
            });
        }

        return () => {
            profileAbortController?.abort();
        };
    }, [orgSettings, fetchProfileImage]);

    // Load admins when Team Members accordion opens
    useEffect(() => {
        if (settingsAccordion.teamMembers && user?.organisationId && admins.length === 0) {
            loadAdmins();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsAccordion.teamMembers]);

    useEffect(() => {
        if (settingsAccordion.emailTemplates && emailTemplates.length === 0) {
            setEmailTemplatesLoading(true);
            organizationApi
                .getEmailTemplates()
                .then(res => {
                    if (res.ok) setEmailTemplates(res.data.templates || []);
                })
                .catch(() => {})
                .finally(() => setEmailTemplatesLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsAccordion.emailTemplates]);

    const reloadEmailTemplates = () => {
        setEmailTemplatesLoading(true);
        organizationApi
            .getEmailTemplates()
            .then(res => {
                if (res.ok) setEmailTemplates(res.data.templates || []);
            })
            .catch(() => {})
            .finally(() => setEmailTemplatesLoading(false));
    };

    useEffect(() => {
        if (settingsAccordion.emailTemplates && studioTemplates.length === 0) {
            setStudioTemplatesLoading(true);
            organizationApi
                .getStudioTemplates()
                .then(res => {
                    if (res.ok) setStudioTemplates(res.data.templates || []);
                })
                .catch(() => {})
                .finally(() => setStudioTemplatesLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsAccordion.emailTemplates]);

    const reloadStudioTemplates = () => {
        setStudioTemplatesLoading(true);
        organizationApi
            .getStudioTemplates()
            .then(res => {
                if (res.ok) setStudioTemplates(res.data.templates || []);
            })
            .catch(() => {})
            .finally(() => setStudioTemplatesLoading(false));
    };

    const handleOpenEditStudioTemplate = template => {
        organizationApi
            .getStudioTemplate(template.id)
            .then(res => {
                if (!res.ok) throw new Error("Failed to load");
                const { id, name, subject, bodyHtml } = res.data.template;
                setEditingStudioTemplate({ id, name, subject, bodyHtml });
            })
            .catch(() => toast.error("Failed to load template."));
    };

    const handleSaveStudioTemplate = async () => {
        const rawContent = studioEditorRef.current?.getContent() ?? "";
        const bodyHtml = tokensToHandlebars(rawContent);
        const { id, name, subject } = editingStudioTemplate;
        if (!name?.trim()) {
            toast.error("Name is required.");
            return;
        }
        if (!subject?.trim()) {
            toast.error("Subject is required.");
            return;
        }
        if (!bodyHtml.trim()) {
            toast.error("Body is required.");
            return;
        }
        setStudioTemplateSaving(true);
        try {
            let res;
            if (id) {
                res = await organizationApi.updateStudioTemplate(id, {
                    name: name.trim(),
                    subject: subject.trim(),
                    bodyHtml,
                });
            } else {
                res = await organizationApi.createStudioTemplate({
                    name: name.trim(),
                    subject: subject.trim(),
                    bodyHtml,
                });
            }
            if (!res.ok) throw new Error(res.data?.message || "Failed to save");
            toast.success(id ? "Template updated." : "Template saved.");
            setEditingStudioTemplate(null);
            reloadStudioTemplates();
        } catch (err) {
            toast.error(err.message || "Failed to save template.");
        } finally {
            setStudioTemplateSaving(false);
        }
    };

    const handleDeleteStudioTemplate = async () => {
        if (!deletingStudioTemplate) return;
        try {
            const res = await organizationApi.deleteStudioTemplate(deletingStudioTemplate.id);
            if (!res.ok) throw new Error("Failed to delete");
            toast.success("Template deleted.");
            setDeletingStudioTemplate(null);
            reloadStudioTemplates();
        } catch {
            toast.error("Failed to delete template.");
        }
    };

    const renderEmailTemplates = () => (
        <div className="settings-accordion-section">
            <div
                className={`settings-accordion-header ${settingsAccordion.emailTemplates ? "expanded" : ""}`}
                onClick={() => toggleAccordionSection("emailTemplates")}
            >
                <div className="accordion-title-group">
                    <h4>Email Templates</h4>
                    <span className="accordion-title-hint">review and customise emails</span>
                </div>
                <span className="accordion-icon">{settingsAccordion.emailTemplates ? "−" : "+"}</span>
            </div>
            {settingsAccordion.emailTemplates && (
                <div className="settings-accordion-content">
                    <p className="accordion-description">
                        Review and customise the emails sent to your students. Owner-initiated emails can be edited;
                        automated emails are system-controlled.
                    </p>
                    {emailTemplatesLoading ? (
                        <p style={{ color: "var(--dl-color-gray-500)", fontSize: "14px" }}>Loading templates…</p>
                    ) : (
                        (() => {
                            const editable = emailTemplates.filter(t => t.ownerInitiated);
                            const automated = emailTemplates.filter(t => !t.ownerInitiated);
                            const renderRow = t => (
                                <div key={t.emailType} className="settings-email-template-row">
                                    <div className="settings-email-template-info">
                                        <span className="settings-email-template-label">
                                            <Icon
                                                path={mdiEmailOutline}
                                                size={0.75}
                                                style={{
                                                    marginRight: 6,
                                                    verticalAlign: "middle",
                                                    color: "var(--dl-color-purple-default)",
                                                }}
                                            />
                                            {t.label}
                                            {t.hasCustom && (
                                                <span className="settings-email-template-custom-badge">Custom</span>
                                            )}
                                        </span>
                                        <span className="settings-email-template-trigger">{t.trigger}</span>
                                    </div>
                                    <div className="settings-email-template-actions">
                                        <button
                                            className="settings-email-template-preview-btn"
                                            onClick={() =>
                                                setPreviewTemplate({ emailType: t.emailType, label: t.label })
                                            }
                                        >
                                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                <Icon path={mdiEyeOutline} size={0.7} />
                                                Preview
                                            </span>
                                        </button>
                                        {t.ownerInitiated ? (
                                            <button
                                                className="settings-email-template-edit-btn"
                                                onClick={() =>
                                                    setEditTemplate({
                                                        emailType: t.emailType,
                                                        label: t.label,
                                                        isCustom: !!t.hasCustom,
                                                    })
                                                }
                                            >
                                                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                    <Icon path={mdiPencilOutline} size={0.7} />
                                                    Edit
                                                </span>
                                            </button>
                                        ) : (
                                            <span className="settings-email-template-system-label">
                                                System Controlled
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                            return (
                                <>
                                    {user?.organisationId && (
                                        <>
                                            <hr
                                                className="settings-email-template-group-divider"
                                                style={{ marginTop: 0 }}
                                            />
                                            <h6 className="settings-email-template-group-heading">
                                                Your saved templates
                                            </h6>
                                            <p className="settings-email-template-group-hint">
                                                Save email content you write frequently. Load saved templates in the
                                                Send Emails composer to pre-fill subject and body.
                                            </p>
                                            <div style={{ marginBottom: 16 }}>
                                                <button
                                                    className="btn-purple"
                                                    onClick={() =>
                                                        setEditingStudioTemplate({
                                                            name: "",
                                                            subject: "",
                                                            bodyHtml: "",
                                                        })
                                                    }
                                                >
                                                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                        <Icon path={mdiPlus} size={0.8} />
                                                        Create Template
                                                    </span>
                                                </button>
                                            </div>
                                            {studioTemplatesLoading ? (
                                                <p style={{ color: "var(--dl-color-gray-500)", fontSize: "14px" }}>
                                                    Loading…
                                                </p>
                                            ) : studioTemplates.length === 0 ? (
                                                <p style={{ color: "var(--dl-color-gray-500)", fontSize: "14px" }}>
                                                    No saved templates yet. Click Create Template to add one.
                                                </p>
                                            ) : (
                                                <div className="settings-studio-template-list">
                                                    {studioTemplates.map(t => (
                                                        <div key={t.id} className="settings-studio-template-row">
                                                            <div className="settings-studio-template-info">
                                                                <span className="settings-studio-template-name">
                                                                    {t.name}
                                                                </span>
                                                                <span className="settings-studio-template-subject">
                                                                    {t.subject}
                                                                </span>
                                                            </div>
                                                            <div className="settings-studio-template-actions">
                                                                <button
                                                                    className="settings-email-template-edit-btn"
                                                                    onClick={() => handleOpenEditStudioTemplate(t)}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "4px",
                                                                        }}
                                                                    >
                                                                        <Icon path={mdiPencilOutline} size={0.7} />
                                                                        Edit
                                                                    </span>
                                                                </button>
                                                                <button
                                                                    className="settings-studio-template-delete-btn"
                                                                    onClick={() =>
                                                                        setDeletingStudioTemplate({
                                                                            id: t.id,
                                                                            name: t.name,
                                                                        })
                                                                    }
                                                                >
                                                                    <span
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "4px",
                                                                        }}
                                                                    >
                                                                        <Icon path={mdiTrashCan} size={0.7} />
                                                                        Delete
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {editable.length > 0 && (
                                        <>
                                            <hr className="settings-email-template-group-divider" />
                                            <h6 className="settings-email-template-group-heading">
                                                Editable templates
                                                <button
                                                    type="button"
                                                    className="email-firing-info-trigger"
                                                    onClick={() => setEmailFiringInfo("editable")}
                                                    onMouseEnter={() => setEmailInfoHovered("editable")}
                                                    onMouseLeave={() => setEmailInfoHovered(null)}
                                                    aria-label="When are editable emails sent?"
                                                    title="When are these sent?"
                                                >
                                                    <Icon
                                                        path={emailInfoHovered === "editable" ? mdiInformation : mdiInformationOutline}
                                                        size={0.85}
                                                        color="var(--dl-color-purple-default)"
                                                    />
                                                </button>
                                            </h6>
                                            <div className="settings-email-templates-list">
                                                {editable.map(renderRow)}
                                            </div>
                                        </>
                                    )}
                                    {automated.length > 0 && (
                                        <>
                                            <hr className="settings-email-template-group-divider" />
                                            <h6 className="settings-email-template-group-heading">
                                                Automated emails (system-controlled)
                                                <button
                                                    type="button"
                                                    className="email-firing-info-trigger"
                                                    onClick={() => setEmailFiringInfo("automated")}
                                                    onMouseEnter={() => setEmailInfoHovered("automated")}
                                                    onMouseLeave={() => setEmailInfoHovered(null)}
                                                    aria-label="When are automated emails sent?"
                                                    title="When are these sent?"
                                                >
                                                    <Icon
                                                        path={emailInfoHovered === "automated" ? mdiInformation : mdiInformationOutline}
                                                        size={0.85}
                                                        color="var(--dl-color-purple-default)"
                                                    />
                                                </button>
                                            </h6>
                                            <p className="settings-email-template-group-hint">
                                                These emails are sent automatically by the system in response to
                                                specific events. You can preview the content but cannot edit it.
                                            </p>
                                            <div className="settings-email-templates-list">
                                                {automated.map(renderRow)}
                                            </div>
                                        </>
                                    )}
                                </>
                            );
                        })()
                    )}
                </div>
            )}
        </div>
    );

    const renderPersonalDetails = () => (
        <div className="settings-accordion-section">
            <div
                className={`settings-accordion-header ${settingsAccordion.personalDetails ? "expanded" : ""}`}
                onClick={() => toggleAccordionSection("personalDetails")}
            >
                <div className="accordion-title-group">
                    <h4>Personal Details</h4>
                    <span className="accordion-title-hint">your name</span>
                </div>
                <span className="accordion-icon">{settingsAccordion.personalDetails ? "−" : "+"}</span>
            </div>
            {settingsAccordion.personalDetails && (
                <div className="settings-accordion-content">
                    <p className="accordion-description">
                        Your name is used to identify you when you enrol in classes or book lessons.
                    </p>
                    <div className="settings-subsection">
                        <div className="form-group">
                            <label className="form-label">
                                First name <span style={{ color: "var(--dl-color-danger)" }}>*</span>
                            </label>
                            <input
                                className="form-input"
                                value={personalDetails.firstName}
                                onChange={e => setPersonalDetails(prev => ({ ...prev, firstName: e.target.value }))}
                                maxLength={50}
                                placeholder="First name"
                                autoCapitalize="words"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">
                                Last name <span style={{ color: "var(--dl-color-danger)" }}>*</span>
                            </label>
                            <input
                                className="form-input"
                                value={personalDetails.lastName}
                                onChange={e => setPersonalDetails(prev => ({ ...prev, lastName: e.target.value }))}
                                maxLength={50}
                                placeholder="Last name"
                                autoCapitalize="words"
                            />
                        </div>
                    </div>
                    <div className="settings-actions">
                        <button
                            className="primary-action-btn"
                            onClick={handleSavePersonalDetails}
                            disabled={isSavingPersonalDetails}
                        >
                            {isSavingPersonalDetails ? "Saving…" : "Save Personal Details"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    const renderTeamMembers = () =>
        user?.organisationId ? (
            <div className="settings-accordion-section">
                <div
                    className={`settings-accordion-header ${settingsAccordion.teamMembers ? "expanded" : ""}`}
                    onClick={() => toggleAccordionSection("teamMembers")}
                >
                    <div className="accordion-title-group">
                        <h4>Team Members</h4>
                        <span className="accordion-title-hint">admins and helpers</span>
                    </div>
                    <span className="accordion-icon">{settingsAccordion.teamMembers ? "−" : "+"}</span>
                </div>
                {settingsAccordion.teamMembers && (
                    <div className="settings-accordion-content">
                        <p className="accordion-description">
                            Add team members so others can help manage your account. Team members log in with their
                            own Dance Localé account and select your account when prompted.
                        </p>
                        <div
                            style={{
                                marginTop: "8px",
                                padding: "10px 12px",
                                background: "var(--dl-color-gray-white)",
                                border: "1px solid var(--dl-color-border)",
                                borderRadius: "8px",
                            }}
                        >
                            <p
                                style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    color: "var(--dl-color-text-muted)",
                                    marginBottom: "8px",
                                }}
                            >
                                Role permissions
                            </p>
                            {[
                                { role: "owner", desc: "Full access — financials, billing, team management" },
                                { role: "admin", desc: "Classes, students, check-in, ticketing — no financials" },
                                { role: "helper", desc: "Check-in and ticket scanning only" },
                            ].map(({ role, desc }) => (
                                <div
                                    key={role}
                                    style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}
                                >
                                    <span className={`settings-role-badge settings-role-badge--${role}`}>
                                        {role.charAt(0).toUpperCase() + role.slice(1)}
                                    </span>
                                    <span style={{ fontSize: "12px", color: "var(--dl-color-text-muted)" }}>
                                        {desc}
                                    </span>
                                </div>
                            ))}
                            <div
                                style={{
                                    marginTop: "8px",
                                    paddingTop: "8px",
                                    borderTop: "1px solid var(--dl-color-border)",
                                }}
                            >
                                <button
                                    onClick={() => navigate("/help/getting-started#team-roles")}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        color: "var(--dl-color-hyperlink)",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
                                    onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
                                >
                                    <Icon path={mdiInformationOutline} size={0.55} /> Learn more about team roles
                                </button>
                            </div>
                        </div>

                        {/* Current admins list */}
                        <div className="settings-subsection" style={{ marginTop: "16px" }}>
                            <h5>Current team</h5>
                            {isLoadingAdmins ? (
                                <p style={{ color: "var(--dl-color-text-muted)", fontSize: "14px" }}>Loading...</p>
                            ) : admins.length === 0 ? (
                                <p style={{ color: "var(--dl-color-text-muted)", fontSize: "14px" }}>
                                    No team members yet.
                                </p>
                            ) : (
                                [...admins]
                                    .sort((a, b) => {
                                        const order = { owner: 0, admin: 1, helper: 2 };
                                        const roleA = a.isOwner ? "owner" : a.role || "helper";
                                        const roleB = b.isOwner ? "owner" : b.role || "helper";
                                        return (order[roleA] ?? 2) - (order[roleB] ?? 2);
                                    })
                                    .map(admin => (
                                        <div key={admin.accountId} className="settings-team-member-row">
                                            <div className="settings-team-member-info">
                                                <span className="settings-team-member-name">
                                                    {admin.displayName || admin.username}
                                                </span>
                                                {(() => {
                                                    const roleLabel = admin.isOwner ? "owner" : admin.role || "helper";
                                                    return (
                                                        <span
                                                            className={`settings-role-badge settings-role-badge--${roleLabel}`}
                                                        >
                                                            {roleLabel === "owner"
                                                                ? "Owner"
                                                                : roleLabel === "admin"
                                                                  ? "Admin"
                                                                  : "Helper"}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <div className="settings-team-member-actions">
                                                {!admin.isOwner && checkPermission("manageTeam") && (
                                                    <select
                                                        className="settings-role-select"
                                                        value={admin.role || "helper"}
                                                        onChange={e =>
                                                            handleChangeAdminRole(admin.accountId, e.target.value)
                                                        }
                                                    >
                                                        <option value="owner">Owner</option>
                                                        <option value="admin">Admin</option>
                                                        <option value="helper">Helper</option>
                                                    </select>
                                                )}
                                                {!admin.isOwner && checkPermission("manageTeam") && (
                                                    <button
                                                        onClick={() =>
                                                            handleRemoveAdmin(
                                                                admin.accountId,
                                                                admin.displayName || admin.username
                                                            )
                                                        }
                                                        title="Remove team member"
                                                        className="settings-team-member-remove-btn"
                                                        onMouseEnter={e => {
                                                            e.currentTarget.style.background = "#ffeaea";
                                                            e.currentTarget.style.borderColor = "#e53935";
                                                            e.currentTarget.style.color = "#e53935";
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.currentTarget.style.background = "none";
                                                            e.currentTarget.style.borderColor = "#ddd";
                                                            e.currentTarget.style.color = "#555";
                                                        }}
                                                    >
                                                        <Icon path={mdiTrashCan} size={0.85} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))
                            )}
                        </div>

                        {/* Search and add */}
                        <div className="settings-subsection" style={{ marginTop: "20px" }}>
                            <h5>Add team member</h5>
                            <div className="form-group" style={{ marginBottom: "12px" }}>
                                <label className="form-label">Role</label>
                                <select
                                    className="settings-role-select"
                                    value={newAdminRole}
                                    onChange={e => setNewAdminRole(e.target.value)}
                                >
                                    <option value="owner">Owner — Full access including financials</option>
                                    <option value="admin">Admin — Full operations, no financials</option>
                                    <option value="helper">Helper — Check-in and scanning only</option>
                                </select>
                            </div>
                            <p className="form-hint">Search by username to find an existing Dance Localé user.</p>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Search by username..."
                                value={adminSearchQuery}
                                onChange={e => handleAdminSearch(e.target.value)}
                                onKeyDown={e =>
                                    e.key === "Enter" && handleAdminSearch(adminSearchQuery, { showErrorToast: true })
                                }
                                style={{ marginTop: "8px" }}
                            />

                            {adminSearchResults.length > 0 && (
                                <div
                                    style={{
                                        marginTop: "12px",
                                        border: "1px solid var(--dl-color-gray-200)",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                    }}
                                >
                                    {adminSearchResults.map(result => (
                                        <div
                                            key={result.id}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                padding: "10px 14px",
                                                borderBottom: "1px solid var(--dl-color-gray-200)",
                                            }}
                                        >
                                            <div>
                                                <span style={{ fontWeight: 500 }}>{result.name}</span>
                                                {result.email && (
                                                    <span
                                                        style={{
                                                            color: "var(--dl-color-text-muted)",
                                                            fontSize: "13px",
                                                            marginLeft: "8px",
                                                        }}
                                                    >
                                                        {result.email}
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                className="btn-purple"
                                                style={{ padding: "4px 12px", fontSize: "13px" }}
                                                onClick={() => handleAddAdmin(result.id)}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {adminSearchResults.length === 0 && adminSearchQuery.length >= 2 && !isSearchingAdmins && (
                                <p style={{ color: "var(--dl-color-text-muted)", fontSize: "14px", marginTop: "8px" }}>
                                    No matching users found.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        ) : null;

    // Render user-type-specific settings
    const renderOrganisationSettings = () => (
        <div className="tab-content-settings">
            {/* Page Header with Back Button */}
            <div className="portal-header-title-row">
                <button onClick={() => navigate("/dashboard")} className="back-nav-btn">
                    ← Dashboard
                </button>
                <h1 className="portal-header-title">Settings</h1>
            </div>

            {/* Featured card selector — pick which nav card appears largest on the Dashboard */}
            <div className="featured-card-picker">
                <p className="featured-card-picker-title">
                    Featured Dashboard card
                </p>
                <div className="featured-card-picker-options">
                    {[
                        { value: "classes", label: "Classes" },
                        { value: "privateLessons", label: "Private Lessons" },
                        { value: "ticketing", label: "Ticketing" },
                        { value: null, label: "None" },
                    ].filter(opt => {
                        // Omit a featured-card option whose mapped feature is currently off
                        // (a disabled feature can't be the hero card). "None" always stays.
                        const HERO_TO_FEATURE = { classes: "classManagement", privateLessons: "privateLessons", ticketing: "ticketing" };
                        return opt.value === null || checkFeature(HERO_TO_FEATURE[opt.value]);
                    }).map(opt => (
                        <CustomFormInput
                            key={String(opt.value)}
                            type="radio"
                            layout="pill"
                            variant="turquoise"
                            id={`hero-card-${String(opt.value)}`}
                            className={opt.value ? "featured-card-picker-card" : ""}
                            name="heroCard"
                            value={String(opt.value)}
                            label={opt.label}
                            checked={orgSettings.dashboardPrimaryCard === opt.value}
                            onChange={() => handleHeroCardChange(opt.value)}
                        />
                    ))}
                </div>
            </div>

            {/* Accordion sections — reorder by changing the call order below */}
            {renderWhatYouDo()}
            {renderOrgInfo()}
            {checkPermission("manageBilling") && renderPaymentSettings()}
            {renderBranding()}
            {renderSupplierPolicies()}
            {renderTeamMembers()}
            {renderEmailTemplates()}
            {renderPersonalDetails()}
            {renderAccountDeletion()}
            <EmailTemplatePreviewModal
                isOpen={!!previewTemplate}
                emailType={previewTemplate?.emailType}
                emailLabel={previewTemplate?.label}
                onClose={() => setPreviewTemplate(null)}
            />
            <EmailTemplateEditorModal
                isOpen={!!editTemplate}
                emailType={editTemplate?.emailType}
                emailLabel={editTemplate?.label}
                isCustom={editTemplate?.isCustom}
                onClose={() => setEditTemplate(null)}
                onSaved={reloadEmailTemplates}
            />
            <EmailFiringInfoModal
                isOpen={!!emailFiringInfo}
                variant={emailFiringInfo || "editable"}
                onClose={() => setEmailFiringInfo(null)}
            />
            <BaseModal
                isOpen={!!editingStudioTemplate}
                onClose={() => setEditingStudioTemplate(null)}
                title={editingStudioTemplate?.id ? "Edit Saved Template" : "Create Saved Template"}
                icon={mdiEmailOutline}
                maxWidth={760}
                footer={
                    <>
                        <button
                            className="base-modal-cancel-btn"
                            onClick={() => setEditingStudioTemplate(null)}
                            disabled={studioTemplateSaving}
                        >
                            Cancel
                        </button>
                        <button
                            className="base-modal-save-btn"
                            onClick={handleSaveStudioTemplate}
                            disabled={studioTemplateSaving}
                        >
                            {studioTemplateSaving ? "Saving…" : "Save Template"}
                        </button>
                    </>
                }
            >
                {editingStudioTemplate && (
                    <>
                        <div style={{ marginBottom: 16 }}>
                            <label className="form-label">Name</label>
                            <p className="form-hint">
                                A short label to identify this template (e.g. "End of term message").
                            </p>
                            <input
                                className="form-input"
                                value={editingStudioTemplate.name}
                                onChange={e => setEditingStudioTemplate(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Template name"
                                maxLength={100}
                            />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label className="form-label">Subject</label>
                            <p className="form-hint">The email subject line sent to recipients.</p>
                            <input
                                className="form-input"
                                value={editingStudioTemplate.subject}
                                onChange={e => setEditingStudioTemplate(prev => ({ ...prev, subject: e.target.value }))}
                                placeholder="Email subject"
                                maxLength={200}
                            />
                        </div>
                        <div>
                            <label className="form-label">Email Body</label>
                            <p className="form-hint">
                                Click "+ Insert Field" to add token chips like Student Name. Tokens resolve at send
                                time.
                            </p>
                            <EmailTemplateEditor
                                key={editingStudioTemplate.id || "new"}
                                ref={studioEditorRef}
                                emailType="broadcast"
                                initialHtml={editingStudioTemplate.bodyHtml || ""}
                                height={320}
                            />
                        </div>
                    </>
                )}
            </BaseModal>
            <ConfirmationModal
                isOpen={!!deletingStudioTemplate}
                onCancel={() => setDeletingStudioTemplate(null)}
                onConfirm={handleDeleteStudioTemplate}
                title="Delete Template"
                message={`Delete "${deletingStudioTemplate?.name}"? This cannot be undone.`}
                confirmText="Delete"
                variant="danger"
            />
        </div>
    );

    const renderPaymentSettings = () => (
        <div className="settings-accordion-section">
            <div
                className={`settings-accordion-header ${settingsAccordion.paymentSettings ? "expanded" : ""}`}
                onClick={() => toggleAccordionSection("paymentSettings")}
            >
                <div className="accordion-title-group">
                    <h4>Payment Settings</h4>
                    <span className="accordion-title-hint">payouts, bank transfer</span>
                </div>
                <span className="accordion-icon">{settingsAccordion.paymentSettings ? "−" : "+"}</span>
            </div>

            {settingsAccordion.paymentSettings && (
                <div className="settings-accordion-content">
                    <div className="settings-subsection">
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "20px",
                                gap: "12px",
                                flexWrap: "wrap",
                            }}
                        >
                            <h5
                                style={{
                                    margin: 0,
                                    color: "var(--dl-color-purple-default)",
                                    fontSize: "calc(18px * var(--scale-factor, 1))",
                                    fontWeight: 700,
                                }}
                            >
                                Receive Payments
                            </h5>
                            <button className="primary-action-btn" onClick={() => navigate("/manage-finances")}>
                                {connectStatus?.status === "active" ? "Manage Payouts" : "Set Up Payouts"}
                            </button>
                        </div>
                        {connectStatusLoading ? (
                            <p style={{ color: "var(--dl-color-text-muted)", fontSize: "14px" }}>
                                Loading payment status...
                            </p>
                        ) : connectStatus?.status === "active" ? (
                            <p className="subsection-description">
                                Your payout account is active. Manage your bank details and payouts in Manage Finances.
                            </p>
                        ) : connectStatus?.status === "restricted" ? (
                            <p className="subsection-description">
                                Your payout account needs verification. Finish setting it up in Manage Finances.
                            </p>
                        ) : (
                            <p className="subsection-description">
                                Add your bank details in Manage Finances to receive automatic payouts for ticket sales and lesson bookings.
                            </p>
                        )}
                    </div>

                    <hr
                        style={{
                            border: "none",
                            borderTop: "1px solid var(--dl-color-gray-300)",
                            margin: "8px 0 24px 0",
                        }}
                    />

                    {/* Bank transfer (free feature) */}
                    <div className="settings-subsection bank-transfer-subsection">
                        <BankTransferAddonCard
                            offeringEnabled={bankTransfer.offeringEnabled}
                            hasBankDetails={
                                !!(
                                    bankTransfer.bankDetails?.bsb &&
                                    bankTransfer.bankDetails?.accountName &&
                                    bankTransfer.bankDetails?.accountNumber
                                )
                            }
                            onChange={loadBankTransferState}
                        />
                        <BankDetailsForm
                            initialBankDetails={bankTransfer.bankDetails}
                            onSaved={loadBankTransferState}
                        />
                    </div>
                </div>
            )}
        </div>
    );

    const renderOrgInfo = () => {
        // Account-type gating: the SCHOOL-specific surfaces (public-profile switch, cover
        // image, school-card preview, dance styles) are a dance-school concept. A pure
        // Dance Artist presents through their own instructor profile (separate page +
        // cover), and a pure Event Organiser ("just sell tickets") has no public school
        // profile. Show those only when the org is a dance school; basic info (name,
        // currency, description) stays for every account type.
        const showSchoolBranding = orgDetails?.isDanceSchool === true;
        // Event organisers aren't dance schools (no public school profile), but they DO need a
        // business location: it sets the timezone their event times are interpreted/displayed in.
        const showBusinessLocation = showSchoolBranding || orgDetails?.isEventOrganiser === true;
        const accountDetailsHint = showSchoolBranding
            ? "public profile, logo, location, styles"
            : showBusinessLocation
              ? "name, currency, location"
              : "name, currency";
        return (
        <div className="settings-accordion-section">
            <div
                className={`settings-accordion-header ${settingsAccordion.organisationInfo ? "expanded" : ""}`}
                onClick={() => toggleAccordionSection("organisationInfo")}
            >
                <div className="accordion-title-group">
                    <h4>Account Details</h4>
                    <span className="accordion-title-hint">{accountDetailsHint}</span>
                </div>
                <span className="accordion-icon">{settingsAccordion.organisationInfo ? "−" : "+"}</span>
            </div>

            {/* Hold the form until the org has loaded: the controls default to empty/unticked
                while orgDetails is null, and an edit made then is overwritten when the load lands. */}
            {settingsAccordion.organisationInfo && !orgDetails && (
                <div className="settings-accordion-content">
                    <p style={{ color: "var(--dl-color-text-muted)", fontSize: "14px" }}>Loading...</p>
                </div>
            )}
            {settingsAccordion.organisationInfo && orgDetails && (
                <div className="settings-accordion-content">
                    {/* Public visibility governs everything below it in this accordion, so it
                        leads the section and is followed by a divider. School-only: a non-school
                        has no directory presence to toggle (see showSchoolBranding). */}
                    {showSchoolBranding && (
                        <div className="settings-org-visibility-lead">
                            <div className="settings-org-info-section-header">
                                <h5 style={{ marginBottom: 0 }}>Public Profile</h5>
                                <label
                                    className="dashboard-toggle-ios"
                                    title={
                                        orgDetails?.isSearchable !== false
                                            ? "Your school is currently visible and searchable by students"
                                            : "Your school is currently hidden, not searchable or displayable to students"
                                    }
                                    style={{ cursor: "pointer" }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={orgDetails?.isSearchable !== false}
                                        onChange={handleToggleSchoolVisibility}
                                    />
                                    <span className="toggle-slider"></span>
                                    <span className="toggle-label" style={{ marginLeft: 8 }}>
                                        Make School Visible
                                    </span>
                                </label>
                            </div>
                            <p className="subsection-description" style={{ marginTop: 0 }}>
                                Controls whether students can find your school in the directory. The
                                details below appear on your public pages when this is on.
                            </p>
                        </div>
                    )}

                    {/* Logo — every account type. It is rendered publicly for all of them:
                        schools on the directory card, event organisers on every event landing
                        page a ticket buyer sees. Organisers who set none fall back to the
                        Dance Localé logo at render time, so this is optional, not required. */}
                    <div className="settings-subsection">
                        <h5>Logo</h5>
                        <p className="subsection-description">
                            {showSchoolBranding
                                ? "Upload your logo. It appears on your school card and your public pages. If you do not add one, the Dance Localé logo is shown instead."
                                : "Upload your logo. It appears on your event pages. If you do not add one, the Dance Localé logo is shown instead."}
                        </p>
                        {/* Logo is a small, roughly square mark — constrain its uploader so it
                            does not render full-bleed and read as a wide banner like the cover. */}
                        <div className="settings-org-logo-upload-constrain">
                            <ImageUpload
                                onImageSelect={imageData => {
                                    setLogoFile(imageData);
                                    setLogoChanged(true);
                                }}
                                currentImage={logoFile?.thumbnail || orgDetails?.logoUrl || null}
                                maxWidth={800}
                                compressionQuality={0.15}
                                thumbnailQuality={0.3}
                            />
                        </div>
                        {logoChanged && (
                            <div className="image-preview-actions">
                                <button
                                    type="button"
                                    className="img-save-btn"
                                    onClick={handleSaveLogo}
                                    disabled={isSavingLogo}
                                >
                                    {isSavingLogo ? "Saving…" : "Save"}
                                </button>
                                <button
                                    type="button"
                                    className="img-cancel-btn"
                                    onClick={handleCancelLogo}
                                    disabled={isSavingLogo}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>

                    {showSchoolBranding && (
                    <>

                    {/* Two-column imagery: cover uploader left, live preview right. The logo
                        uploader is above, outside this block — it applies to every account type. */}
                    <div className="settings-org-info-imagery-layout">
                        <div className="settings-org-info-imagery-fields">
                            {/* Cover Image */}
                            <div className="settings-subsection">
                                <h5>Cover Image</h5>
                                <p className="subsection-description">
                                    The banner image on your public page. The preview shows exactly what students
                                    see on the school directory.
                                </p>
                                <ImageUpload
                                    onImageSelect={imageData => {
                                        setCoverImageFile(imageData);
                                        setCoverImageChanged(true);
                                    }}
                                    currentImage={coverImageFile?.thumbnail || orgDetails?.coverImageUrl || null}
                                    maxWidth={1200}
                                    compressionQuality={0.2}
                                    thumbnailQuality={0.3}
                                />
                                {coverImageChanged ? (
                                    <div className="image-preview-actions">
                                        <button
                                            type="button"
                                            className="img-save-btn"
                                            onClick={handleSaveCover}
                                            disabled={isSavingCover}
                                        >
                                            {isSavingCover ? "Saving…" : "Save"}
                                        </button>
                                        <button
                                            type="button"
                                            className="img-cancel-btn"
                                            onClick={handleCancelCover}
                                            disabled={isSavingCover}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : orgDetails?.coverImageUrl && (
                                    <div className="image-preview-actions">
                                        <button
                                            type="button"
                                            className="btn-danger btn-small"
                                            onClick={handleRemoveCover}
                                            disabled={isRemovingCover}
                                        >
                                            {isRemovingCover ? "Removing…" : "Remove Cover"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* end settings-org-info-imagery-fields */}

                        <div className="settings-org-info-preview-wrapper">
                            <p className="settings-org-info-preview-label">Student-facing school card preview</p>
                            <SchoolCard
                                school={{
                                    name: orgDetails?.name || "Your School Name",
                                    description: orgDetails?.businessBlurb || null,
                                    logoBase64: logoFile?.thumbnail || orgDetails?.logoUrl || null,
                                    coverBase64: coverImageFile?.thumbnail || orgDetails?.coverImageUrl || null,
                                    location: orgDetails?.orgLocation || null,
                                    styles: orgDetails?.businessDanceStyles || [],
                                }}
                                isPreview
                            />
                        </div>
                    </div>
                    {/* end settings-org-info-imagery-layout */}
                    </>
                    )}

                    {/* Basic Information — single column below imagery */}
                    <div className="settings-subsection">
                        <h5>Basic Information</h5>
                        <div className="form-group">
                            <label className="form-label">
                                Name <span style={{ color: "var(--dl-color-danger)" }}>*</span>
                            </label>
                            {!showSchoolBranding && (
                                <p className="form-hint">Use your own name if you are not using a separate trading name.</p>
                            )}
                            <input
                                className="form-input"
                                value={orgDetails?.name || ""}
                                onChange={e => setOrgDetails(prev => ({ ...prev, name: e.target.value }))}
                                maxLength={200}
                                placeholder="Your name or school name"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Base Currency</label>
                            <p className="form-hint">Currency for lesson payments, class fees, and invoicing.</p>
                            <select
                                className="form-select"
                                value={orgDetails?.currency || "AUD"}
                                onChange={e => setOrgDetails(prev => ({ ...prev, currency: e.target.value }))}
                            >
                                {currencyOptions.map(curr => (
                                    <option key={curr.code} value={curr.code}>
                                        {curr.symbol} {curr.code} - {curr.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Description</label>
                            <textarea
                                className="form-textarea"
                                value={orgDetails?.businessBlurb || ""}
                                onChange={e => setOrgDetails(prev => ({ ...prev, businessBlurb: e.target.value }))}
                                maxLength={500}
                                rows={3}
                                placeholder="A short description (optional)"
                            />
                            <span className="form-hint">{(orgDetails?.businessBlurb || "").length}/500</span>
                        </div>
                        {/* Section-local save (matches InstructorProfilePage 'Save Profile Image + Name' pattern).
                            Resolves the live-preview-but-no-visible-save ambiguity for typed fields. */}
                        <div style={{ marginTop: "8px" }}>
                            <button
                                className="primary-action-btn"
                                onClick={handleSaveOrgDetails}
                                disabled={isSavingOrgDetails || !orgDetails}
                            >
                                {isSavingOrgDetails ? "Saving…" : "Save Profile Info"}
                            </button>
                        </div>
                    </div>

                    {showBusinessLocation && (
                    /* Business Location */
                    <div className="settings-subsection">
                        <h5>Location</h5>
                        <p className="subsection-description">
                            {showSchoolBranding
                                ? "Set your primary address where you operate from, or a registered business address. This will be geocoded and used for location-based discovery."
                                : "Set your location. This sets the timezone your event times are shown in."}
                        </p>
                        <button className="secondary-action-btn" onClick={() => setShowBusinessLocationPicker(true)}>
                            {orgDetails?.orgLocation?.description ? "Change Location" : "Set Location"}
                        </button>
                        {orgDetails?.orgLocation?.description && (
                            <p className="form-hint" style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "4px" }}>
                                <Icon path={mdiMapMarker} size={0.7} />
                                {orgDetails.orgLocation.description}
                            </p>
                        )}
                        <LocationPickerModal
                            isOpen={showBusinessLocationPicker}
                            onClose={() => setShowBusinessLocationPicker(false)}
                            onSave={location => {
                                setOrgDetails(prev => ({ ...prev, orgLocation: location }));
                                setOrgLocationChanged(true);
                                setShowBusinessLocationPicker(false);
                            }}
                            currentLocation={orgDetails?.orgLocation || null}
                            mode="address"
                            title="Set Location"
                        />
                    </div>
                    )}

                    {showSchoolBranding && (
                    /* Dance Styles */
                    <div className="settings-subsection">
                        <h5>Dance Styles</h5>
                        <p className="subsection-description">Select the dance styles your business offers.</p>
                        <DanceStyleChips
                            selected={orgDetails?.businessDanceStyles || []}
                            onChange={updated =>
                                setOrgDetails(prev => ({ ...prev, businessDanceStyles: updated }))
                            }
                        />
                    </div>
                    )}

                    {/* Save Button */}
                    <div className="settings-actions">
                        <button
                            className="primary-action-btn"
                            onClick={handleSaveOrgDetails}
                            disabled={isSavingOrgDetails || !orgDetails}
                        >
                            {isSavingOrgDetails ? "Saving…" : "Save Account Details"}
                        </button>
                    </div>
                </div>
            )}
        </div>
        );
    };

    const renderSupplierPolicies = () => {
        // Class-enrolment terms are a dance-school concept; private-lesson terms live on
        // the instructor profile page (Private Booking Defaults). Gate by account type so
        // only dance schools see this; it re-evaluates if the org toggles its type later.
        if (orgDetails?.isDanceSchool !== true) return null;

        const updateField = (key, value) => {
            setPolicies(prev => ({
                ...prev,
                classEnrolments: { ...(prev?.classEnrolments || {}), [key]: value },
            }));
        };

        return (
            <div className="settings-accordion-section">
                <div
                    className={`settings-accordion-header ${settingsAccordion.supplierPolicies ? "expanded" : ""}`}
                    onClick={() => toggleAccordionSection("supplierPolicies")}
                >
                    <div className="accordion-title-group">
                        <h4>Terms and Conditions</h4>
                        <span className="accordion-title-hint">shown at checkout</span>
                    </div>
                    <span className="accordion-icon">{settingsAccordion.supplierPolicies ? "−" : "+"}</span>
                </div>
                {settingsAccordion.supplierPolicies && (
                    <div className="settings-accordion-content">
                        <p className="accordion-description">
                            These are your class enrolment terms and conditions. They are shown to customers at
                            checkout, where they tick a box to agree before paying. Each field is pre-filled with
                            suggested wording you can keep or edit.
                        </p>
                        {policies ? (
                            <div className="settings-subsection">
                                {SUPPLIER_POLICY_EDITOR_FIELDS.map(({ key, label }) => (
                                    <div className="form-group" key={key}>
                                        <label className="form-label">{label}</label>
                                        <textarea
                                            className="form-textarea"
                                            value={policies?.classEnrolments?.[key] || ""}
                                            onChange={e => updateField(key, e.target.value)}
                                            maxLength={2000}
                                            rows={2}
                                        />
                                    </div>
                                ))}
                                <button
                                    className="btn-purple"
                                    onClick={handleSavePolicies}
                                    disabled={isSavingPolicies}
                                >
                                    {isSavingPolicies ? "Saving..." : "Save Terms and Conditions"}
                                </button>
                            </div>
                        ) : (
                            <p className="form-hint">Loading terms and conditions...</p>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderBranding = () => (
        <div className="settings-accordion-section">
            <div
                className={`settings-accordion-header ${settingsAccordion.brandingSettings ? "expanded" : ""}`}
                onClick={() => toggleAccordionSection("brandingSettings")}
            >
                <div className="accordion-title-group">
                    <h4>Branding</h4>
                    <span className="accordion-title-hint">colours for your public pages</span>
                </div>
                <span className="accordion-icon">{settingsAccordion.brandingSettings ? "−" : "+"}</span>
            </div>
            {settingsAccordion.brandingSettings && (
                <div className="settings-accordion-content">
                    <p className="accordion-description">
                        Branding applies to your public class enrolment pages for enquiries for class enrolments and for
                        purchasing page flows.
                    </p>
                    <div className="settings-branding-grid">
                        <div className="settings-branding-form">
                            {[
                                { key: "headerColor", label: "Header Colour", hint: "Header bar background" },
                                { key: "headerTextColor", label: "Header Text Colour", hint: "Header text and icons" },
                                {
                                    key: "backgroundColor",
                                    label: "Background Colour",
                                    hint: "Page and card backgrounds",
                                },
                                { key: "textColor", label: "Text Colour", hint: "Body copy on pages" },
                                { key: "buttonColor", label: "Button Colour", hint: "Enrol / book button background" },
                                {
                                    key: "buttonTextColor",
                                    label: "Button Text Colour",
                                    hint: "Enrol / book button labels",
                                },
                            ].map(({ key, label, hint }) => (
                                <div key={key} className="settings-branding-field">
                                    <label className="settings-branding-label">{label}</label>
                                    <p className="settings-branding-hint">{hint}</p>
                                    <div className="settings-colour-row">
                                        <div
                                            className="settings-colour-swatch"
                                            style={{
                                                background: /^#[0-9A-Fa-f]{6}$/.test(branding[key])
                                                    ? branding[key]
                                                    : "#eee",
                                            }}
                                            onClick={() => {
                                                const el = document.getElementById(`settings-colour-${key}`);
                                                if (el) el.click();
                                            }}
                                        />
                                        <input
                                            id={`settings-colour-${key}`}
                                            type="color"
                                            value={/^#[0-9A-Fa-f]{6}$/.test(branding[key]) ? branding[key] : "#000000"}
                                            onChange={e => setBranding(prev => ({ ...prev, [key]: e.target.value }))}
                                            style={{
                                                position: "absolute",
                                                opacity: 0,
                                                width: 0,
                                                height: 0,
                                                pointerEvents: "none",
                                            }}
                                        />
                                        <input
                                            className="settings-colour-input"
                                            value={branding[key]}
                                            onChange={e => setBranding(prev => ({ ...prev, [key]: e.target.value }))}
                                            placeholder="#000000"
                                            maxLength={7}
                                            spellCheck={false}
                                        />
                                    </div>
                                </div>
                            ))}
                            <button
                                className="primary-action-btn"
                                onClick={handleSaveBranding}
                                disabled={isSavingBranding}
                                style={{ marginTop: 4 }}
                            >
                                {isSavingBranding ? "Saving..." : "Save Branding"}
                            </button>
                        </div>

                        {/* Live preview */}
                        <div className="settings-branding-preview">
                            <div
                                className="settings-preview-header"
                                style={{ background: branding.headerColor, color: branding.headerTextColor }}
                            >
                                <p className="settings-preview-title" style={{ color: branding.headerTextColor }}>
                                    Your Dance School
                                </p>
                                <p className="settings-preview-subtitle" style={{ color: branding.headerTextColor }}>
                                    Beginner · Intermediate · Advanced
                                </p>
                            </div>
                            <div className="settings-preview-body" style={{ background: branding.backgroundColor }}>
                                <div
                                    className="settings-preview-card"
                                    style={{ background: "#fff", borderColor: branding.headerColor + "33" }}
                                >
                                    <p className="settings-preview-card-title" style={{ color: branding.textColor }}>
                                        Salsa Beginners
                                    </p>
                                    <p className="settings-preview-card-meta">Monday · 7:00 PM</p>
                                </div>
                                <div
                                    className="settings-preview-card"
                                    style={{ background: "#fff", borderColor: branding.headerColor + "33" }}
                                >
                                    <p className="settings-preview-card-title" style={{ color: branding.textColor }}>
                                        Bachata Intermediate
                                    </p>
                                    <p className="settings-preview-card-meta">Wednesday · 8:00 PM</p>
                                </div>
                                <button
                                    className="settings-preview-btn"
                                    style={{ background: branding.buttonColor, color: branding.buttonTextColor }}
                                >
                                    Enrol Now
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Render settings based on user type
    const sendDeletionAssistance = async () => {
        setIsRequestingAssistance(true);
        try {
            const response = await accountApi.requestDeletionAssistance();
            if (response.ok && response.data?.success) {
                setDeletionEligibility(prev => ({ ...prev, assistanceRequestedAt: response.data.requestedAt }));
                toast.success(response.data.message);
            } else {
                toast.error(response.data?.message || "Could not send your request. Please try again.");
            }
        } catch (error) {
            console.error("Deletion assistance request failed:", error);
            toast.error("Could not send your request. Please try again.");
        } finally {
            setIsRequestingAssistance(false);
        }
    };

    const handleRequestDeletionAssistance = () => {
        setConfirmModalConfig({
            title: "Send deletion request to support?",
            message:
                `We will email our team asking them to close or transfer your ${deletionEligibility?.blockingOrg?.typeLabel} so your account can then be deleted. They will contact you to arrange it.`,
            confirmText: "Send request",
            variant: "danger",
            onConfirm: async () => {
                setShowConfirmModal(false);
                await sendDeletionAssistance();
            },
        });
        setShowConfirmModal(true);
    };

    const handleRequestAccountDeletion = () => {
        setConfirmModalConfig({
            title: "Request Account Deletion?",
            message:
                "Your account will be scheduled for permanent deletion in 30 days. You can cancel the request at any time before then by logging back in or using the emailed link.",
            confirmText: "Request Deletion",
            variant: "danger",
            onConfirm: async () => {
                setShowConfirmModal(false);
                try {
                    const response = await accountApi.requestAccountDeletion();
                    if (response.ok && response.data?.success) {
                        const deletionDate = response.data.deletionDate || "";
                        logout();
                        // Hard navigation: an SPA navigate() lets Settings re-render once with a
                        // null user, refiring its loaders against a cleared token and surfacing a
                        // stray "Failed to load ..." toast over the result page.
                        window.location.replace(`/deletion-requested?date=${encodeURIComponent(deletionDate)}`);
                    } else if (response.data?.code === "OWNER_OF_NONEMPTY_ORG") {
                        toast.error(response.data.message);
                    } else {
                        toast.error(response.data?.message || "Could not schedule your account deletion. Please try again.");
                    }
                } catch (error) {
                    console.error("Account deletion request failed:", error);
                    toast.error("Could not schedule your account deletion. Please try again.");
                }
            },
        });
        setShowConfirmModal(true);
    };

    // Matches the surrounding accordion sections; only the heading text is red to
    // mark it as the destructive section.
    const renderAccountDeletion = () => (
        <div className="settings-accordion-section">
            <div
                className={`settings-accordion-header ${settingsAccordion.accountDeletion ? "expanded" : ""}`}
                onClick={() => toggleAccordionSection("accountDeletion")}
            >
                <div className="accordion-title-group">
                    <h4 style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--dl-color-danger)" }}>
                        <Icon path={mdiAlertOutline} size={0.7} />
                        Request account deletion
                    </h4>
                    <span className="accordion-title-hint">permanently remove your account</span>
                </div>
                <span className="accordion-icon">{settingsAccordion.accountDeletion ? "−" : "+"}</span>
            </div>
            {settingsAccordion.accountDeletion && (
                <div className="settings-accordion-content">
                    {deletionEligibility && !deletionEligibility.canDelete ? (
                        <>
                            <p className="accordion-description">
                                {`You cannot delete this account while you are the only owner of the ${deletionEligibility.blockingOrg?.typeLabel} that still has active records (${deletionEligibility.blockingOrg?.reason || "active records"}). Deleting it would strand the people who booked or enrolled with you, along with the financial records that must be kept.`}
                            </p>
                            <div
                                className="settings-subsection"
                                style={{
                                    backgroundColor: "var(--dl-color-yellow-50)",
                                    border: "1px solid var(--dl-color-yellow-100)",
                                    borderRadius: "8px",
                                    padding: "12px",
                                }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        fontWeight: "600",
                                        color: "var(--dl-color-status-amber)",
                                        marginBottom: "6px",
                                        fontSize: "14px",
                                    }}>
                                    <Icon path={mdiAlertOutline} size={0.7} />
                                    {deletionEligibility.blockingOrg?.name ||
                                        `Your ${deletionEligibility.blockingOrg?.typeLabel}`}
                                </div>
                                <p style={{ color: "var(--dl-color-status-amber)", fontSize: "14px", lineHeight: "1.5", margin: 0 }}>
                                    {`Send your deletion request to our team and we will contact you to close or transfer the ${deletionEligibility.blockingOrg?.typeLabel} so your account can then be deleted.`}
                                </p>
                            </div>
                            <div className="settings-subsection">
                                {deletionEligibility.assistanceRequestedAt ? (
                                    <p style={{ fontSize: "14px", color: "var(--dl-color-gray-700)", margin: 0 }}>
                                        Request sent on{" "}
                                        {new Date(deletionEligibility.assistanceRequestedAt).toLocaleDateString("en-AU", {
                                            day: "numeric",
                                            month: "long",
                                            year: "numeric",
                                        })}
                                        . Our team will be in touch.
                                    </p>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn-danger"
                                        onClick={handleRequestDeletionAssistance}
                                        disabled={isRequestingAssistance}>
                                        {isRequestingAssistance ? "Sending..." : "Send deletion request to support"}
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="accordion-description">
                                Requesting deletion schedules your account for permanent removal after a 30 day grace
                                period. You can cancel the request at any time before then by logging back in or using
                                the emailed link. Read the{" "}
                                <a
                                    href="/privacy-policy"
                                    style={{ color: "var(--dl-color-purple-default)", textDecoration: "underline" }}>
                                    Privacy Policy
                                </a>{" "}
                                for what is removed and what is retained.
                            </p>
                            <div className="settings-subsection">
                                <button
                                    type="button"
                                    className="btn-danger"
                                    onClick={handleRequestAccountDeletion}
                                    disabled={!deletionEligibility}>
                                    {deletionEligibility ? "Request account deletion" : "Checking..."}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );

    const renderSettings = () => {
        if (!user) {
            return <div>Loading user data...</div>;
        }

        return renderOrganisationSettings();
    };

    return (
        <div className="dashboard-container org-console org-settings-page">
            {/* Standard Navigation Header */}
            <AppHeader />

            {/* Main Content */}
            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">{renderSettings()}</div>
            </div>

            {/* Template Selection Modal — not implemented for MVP */}

            {/* Location Picker Modal for Instructor Settings */}
            <LocationPickerModal
                isOpen={showLocationPicker}
                onClose={() => setShowLocationPicker(false)}
                onSave={handleLocationSave}
                currentLocation={homeLocation}
                mode="home"
                title="Set Home Location"
            />

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showConfirmModal}
                title={confirmModalConfig.title}
                message={confirmModalConfig.message}
                confirmText={confirmModalConfig.confirmText}
                cancelText="Cancel"
                onConfirm={confirmModalConfig.onConfirm}
                onCancel={() => setShowConfirmModal(false)}
                variant={confirmModalConfig.variant}
            />
        </div>
    );
};

export default Settings;
